import { useEffect, useMemo, useState } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import axios from 'axios';
import { useOrganizationSelect } from '../../context/OrganizationSelectContext';
import { useSelector } from 'react-redux';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
});

const emptyForm = {
  firstname: '',
  lastname: '',
  email: '',
  password: '',
  role: 'client',
  provider: 'local',
  status: 'active',
  access: 'read',
  applicationAccess: false,
  cbxApplicationAccess: false,
  quote: false,
  invoice: false,
  jobTitle: '',
  mainAddress: '',
  fiscalCv: '',
  organizationId: '',
  phonesText: '',
  emailsText: '',
};

const parseCsvToArray = (value) =>
  String(value || '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

const Users = () => {
  const [filteredUsers, setFilteredUsers] = useState([]);
  const { data: users = [], loading } = useSelector((state) => state.users.users);
  const { selectedOrganization } = useOrganizationSelect();

  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('token');

  const authConfig = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
    [token],
  );

  useEffect(() => {
    if (selectedOrganization) {
      setFilteredUsers(
        users.filter((item) => (item.organizationDetails?.name || '') === selectedOrganization),
      );
    } else {
      setFilteredUsers(users);
    }
  }, [selectedOrganization, users]);

  const openCreateDialog = () => {
    setIsEdit(false);
    setEditingId(null);
    setForm(emptyForm);
    setOpenDialog(true);
  };

  const openEditDialog = (user) => {
    setIsEdit(true);
    setEditingId(user._id);
    setForm({
      firstname: user.firstname || '',
      lastname: user.lastname || '',
      email: user.email || '',
      password: '',
      role: user.role || 'client',
      provider: user.provider || 'local',
      status: user.status || 'active',
      access: user.access || 'read',
      applicationAccess: !!user.applicationAccess,
      cbxApplicationAccess: !!user.cbxApplicationAccess,
      quote: !!user.quote,
      invoice: !!user.invoice,
      jobTitle: user.jobTitle || '',
      mainAddress: user.mainAddress || '',
      fiscalCv: user.fiscalCv ?? '',
      organizationId: user.organizationDetails?._id || user.Organization || '',
      phonesText: Array.isArray(user.phones) ? user.phones.join(', ') : '',
      emailsText: Array.isArray(user.emails) ? user.emails.join(', ') : '',
    });
    setOpenDialog(true);
  };

  const closeDialog = () => {
    if (submitting) return;
    setOpenDialog(false);
  };

  const upsertLocalUser = (updatedUser) => {
    setFilteredUsers((prev) => {
      const exists = prev.some((u) => u._id === updatedUser._id);
      if (!exists) return [updatedUser, ...prev];
      return prev.map((u) => (u._id === updatedUser._id ? updatedUser : u));
    });
  };

  const handleSave = async () => {
    try {
      setSubmitting(true);

      const payload = {
        firstname: form.firstname,
        lastname: form.lastname,
        email: form.email,
        password: form.password || undefined,
        role: form.role,
        provider: form.provider,
        status: form.status,
        access: form.access,
        applicationAccess: !!form.applicationAccess,
        cbxApplicationAccess: !!form.cbxApplicationAccess,
        quote: !!form.quote,
        invoice: !!form.invoice,
        jobTitle: form.jobTitle || null,
        mainAddress: form.mainAddress || null,
        fiscalCv: form.fiscalCv === '' ? null : Number(form.fiscalCv),
        organizationId: form.organizationId || null,
        phones: parseCsvToArray(form.phonesText),
        emails: parseCsvToArray(form.emailsText),
      };

      let response;
      if (isEdit && editingId) {
        response = await api.put(`/api/user/${editingId}`, payload, authConfig);
      } else {
        response = await api.post('/api/user', payload, authConfig);
      }

      upsertLocalUser(response.data);
      setOpenDialog(false);
    } catch (err) {
      console.error('Erreur save user:', err);
      alert(err?.response?.data?.error || 'Erreur lors de la sauvegarde utilisateur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (user) => {
    try {
      await api.delete(`/api/user/${user._id}`, authConfig);
      setFilteredUsers((prev) =>
        prev.map((u) =>
          u._id === user._id ? { ...u, deleted: new Date().toISOString(), status: 'inactive' } : u,
        ),
      );
    } catch (err) {
      console.error('Erreur delete user:', err);
      alert(err?.response?.data?.error || 'Erreur lors de la suppression utilisateur');
    }
  };

  const handleRestore = async (user) => {
    try {
      const res = await api.post(`/api/user/${user._id}/restore`, {}, authConfig);
      upsertLocalUser(res.data);
    } catch (err) {
      console.error('Erreur restore user:', err);
      alert(err?.response?.data?.error || 'Erreur lors de la restauration utilisateur');
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'users',
        columns: [
          { accessorKey: 'firstname', header: 'Prénom', size: 140 },
          { accessorKey: 'lastname', header: 'Nom', size: 140 },
          { accessorKey: 'email', header: 'Email', size: 220 },
          {
            accessorFn: (row) => row.organizationDetails?.name || '',
            id: 'organizationName',
            header: 'Client',
            size: 180,
          },
          { accessorKey: 'jobTitle', header: 'Poste', size: 140 },
          { accessorKey: 'mainAddress', header: 'Adresse', size: 180 },
          { accessorKey: 'fiscalCv', header: 'CV fiscaux', size: 80 },
          {
            accessorFn: (row) => (Array.isArray(row.phones) ? row.phones.join(', ') : ''),
            id: 'phones',
            header: 'Téléphones',
            size: 180,
          },
          {
            accessorFn: (row) => (Array.isArray(row.emails) ? row.emails.join(', ') : ''),
            id: 'emails',
            header: 'Emails secondaires',
            size: 220,
          },
          {
            accessorKey: 'role',
            header: 'Rôle',
            size: 90,
            Cell: ({ row }) =>
              row.original.role === 'admin' ? (
                <Chip label="Admin" color="primary" size="small" />
              ) : (
                <Chip label="Client" color="secondary" size="small" />
              ),
          },
          {
            accessorFn: (row) => (row.applicationAccess ? 'Oui' : 'Non'),
            id: 'applicationAccess',
            header: 'Accès app',
            size: 70,
            Cell: ({ row }) =>
              row.original.applicationAccess ? (
                <CheckCircleIcon color="primary" />
              ) : (
                <HighlightOffIcon />
              ),
          },
          {
            accessorFn: (row) => (row.deleted ? 'Supprimé' : 'Actif'),
            id: 'state',
            header: 'État',
            size: 90,
          },
        ],
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: filteredUsers || [],
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    enableRowSelection: true,
    enableRowActions: true,
    initialState: {
      density: 'compact',
      columnPinning: {
        left: ['mrt-row-expand', 'mrt-row-select'],
        right: ['mrt-row-actions'],
      },
      pagination: { pageSize: 50 },
    },
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    muiSearchTextFieldProps: { size: 'small', variant: 'outlined' },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [50, 100, 150],
      shape: 'rounded',
      variant: 'outlined',
    },
    renderTopToolbarCustomActions: () => (
      <Button startIcon={<AddIcon />} variant="contained" onClick={openCreateDialog}>
        Nouvel utilisateur
      </Button>
    ),
    renderRowActions: ({ row }) => {
      const user = row.original;
      return (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Modifier">
            <IconButton onClick={() => openEditDialog(user)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          {!user.deleted ? (
            <Tooltip title="Supprimer">
              <IconButton color="error" onClick={() => handleDelete(user)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Restaurer">
              <IconButton color="success" onClick={() => handleRestore(user)}>
                <RestoreFromTrashIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      );
    },
  });

  return (
    <Box>
      <Typography variant="h6" my={1} mb={3}>
        Utilisateurs
      </Typography>

      {loading ? (
        <Skeleton variant="rectangular" width="100%" height={600} />
      ) : filteredUsers.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 2 }}>
          <Typography variant="h6" color="textSecondary">
            Aucune donnée disponible
          </Typography>
        </Box>
      ) : (
        <MaterialReactTable table={table} />
      )}

      <Dialog open={openDialog} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{isEdit ? 'Modifier utilisateur' : 'Créer utilisateur'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Prénom"
              value={form.firstname}
              onChange={(e) => setForm((f) => ({ ...f, firstname: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Nom"
              value={form.lastname}
              onChange={(e) => setForm((f) => ({ ...f, lastname: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label={isEdit ? 'Nouveau mot de passe (optionnel)' : 'Mot de passe'}
              type="password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Poste"
              value={form.jobTitle}
              onChange={(e) => setForm((f) => ({ ...f, jobTitle: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Adresse principale"
              value={form.mainAddress}
              onChange={(e) => setForm((f) => ({ ...f, mainAddress: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Chevaux fiscaux"
              type="number"
              value={form.fiscalCv}
              onChange={(e) => setForm((f) => ({ ...f, fiscalCv: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Organization ID"
              value={form.organizationId}
              onChange={(e) => setForm((f) => ({ ...f, organizationId: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Téléphones (séparés par des virgules)"
              value={form.phonesText}
              onChange={(e) => setForm((f) => ({ ...f, phonesText: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Emails secondaires (séparés par des virgules)"
              value={form.emailsText}
              onChange={(e) => setForm((f) => ({ ...f, emailsText: e.target.value }))}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Rôle</InputLabel>
              <Select
                label="Rôle"
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              >
                <MenuItem value="client">Client</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Provider</InputLabel>
              <Select
                label="Provider"
                value={form.provider}
                onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value }))}
              >
                <MenuItem value="local">Local</MenuItem>
                <MenuItem value="microsoft">Microsoft</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select
                label="Statut"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Accès</InputLabel>
              <Select
                label="Accès"
                value={form.access}
                onChange={(e) => setForm((f) => ({ ...f, access: e.target.value }))}
              >
                <MenuItem value="read">Read</MenuItem>
                <MenuItem value="write">Write</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={form.applicationAccess}
                  onChange={(e) => setForm((f) => ({ ...f, applicationAccess: e.target.checked }))}
                />
              }
              label="Accès application"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.cbxApplicationAccess}
                  onChange={(e) => setForm((f) => ({ ...f, cbxApplicationAccess: e.target.checked }))}
                />
              }
              label="Accès CBX application"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.quote}
                  onChange={(e) => setForm((f) => ({ ...f, quote: e.target.checked }))}
                />
              }
              label="Accès devis"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.invoice}
                  onChange={(e) => setForm((f) => ({ ...f, invoice: e.target.checked }))}
                />
              }
              label="Accès factures"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>
            Annuler
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={submitting}>
            {submitting ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
